import axiosInstance from "../../../common/axiosInstance";
import { AxiosResponse } from "axios";
import { handleApiError } from "../../../common/utils";
import {
  Incident,
  IncidentAction,
  RCAData,
  DashboardStatsResponse,
  ApiResponse,
  EmailData,
  SearchFilters,
  PaginationParams,
} from "../types/types";
import { fetchICSIncidents } from "../../ExternalEmergencyPreparedness/ICS/services/service";

// ============================================================================
// INCIDENT SERVICES
// ============================================================================

/**
 * Fetch incidents with optional filtering parameters
 */
export const fetchIncidents = async (params?: {
  department?: string;
  status?: string;
  statusIn?: string;
  search?: string;
  limit?: number;
  skip?: number;
  hospitalName?: string;
  region?: string;
}): Promise<ApiResponse<Incident[]>> => {
  try {
    let url = `/api/incidents`;

    if (params) {
      const queryParams = new URLSearchParams();

      if (params.department)
        queryParams.append("department", params.department);
      if (params.status) queryParams.append("status", params.status);
      if (params.statusIn) queryParams.append("statusIn", params.statusIn);
      if (params.search) queryParams.append("search", params.search);
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.skip) queryParams.append("skip", params.skip.toString());
      if (params.hospitalName)
        queryParams.append("hospitalName", params.hospitalName);
      if (params.region) queryParams.append("region", params.region);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<ApiResponse<Incident[]>> =
      await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch incidents");
    throw error;
  }
};

/**
 * Fetch incidents by department using a specific API endpoint.
 * This generic function replaces the previous per-department functions.
 */
export const fetchIncidentsByEndpoint = async (
  endpointBase: string,
  department: string,
): Promise<ApiResponse<Incident[]>> => {
  try {
    const response: AxiosResponse<ApiResponse<Incident[]>> =
      await axiosInstance.get(
        `${endpointBase}/${encodeURIComponent(department)}`,
      );

    return response.data;
  } catch (error) {
    handleApiError(
      error,
      `Failed to fetch incidents for department: ${department}`,
    );
    throw error;
  }
};

/**
 * Fetch incidents by department (fire safety / general)
 * @deprecated Use fetchIncidentsByEndpoint instead
 */
export const fetchIncidentsByDepartment = async (
  department: string,
): Promise<ApiResponse<Incident[]>> => {
  return fetchIncidentsByEndpoint("/api/incidents/department", department);
};

/**
 * Fetch washroom incidents by department
 * @deprecated Use fetchIncidentsByEndpoint instead
 */
export const fetchWashroomIncidentsByDepartment = async (
  department: string,
): Promise<ApiResponse<Incident[]>> => {
  return fetchIncidentsByEndpoint(
    "/api/washroomincidents/department",
    department,
  );
};

/**
 * Fetch equipment incidents by department
 * @deprecated Use fetchIncidentsByEndpoint instead
 */
export const fetchEquipmentIncidentsByDepartment = async (
  department: string,
): Promise<ApiResponse<Incident[]>> => {
  return fetchIncidentsByEndpoint(
    "/api/equipmentincidents/department",
    department,
  );
};

/**
 * Get single incident by ID
 */
export const getIncidentById = async (
  id: string,
): Promise<ApiResponse<Incident>> => {
  try {
    const response: AxiosResponse<ApiResponse<Incident>> =
      await axiosInstance.get(`/api/incidents/${id}`);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch incident data");
    throw error;
  }
};

/**
 * Create new incident
 */
export const createIncident = async (
  incidentData: any,
): Promise<ApiResponse<Incident>> => {
  try {
    const response: AxiosResponse<ApiResponse<Incident>> =
      await axiosInstance.post(`/api/incidents`, {
        ...incidentData,
        status: "Open",
      });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create incident");
    throw error;
  }
};

/**
 * Update existing incident
 */
export const updateIncident = async (
  id: string,
  incidentData: any,
): Promise<ApiResponse<Incident>> => {
  try {
    const response: AxiosResponse<ApiResponse<Incident>> =
      await axiosInstance.put(`/api/incidents/${id}`, incidentData);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update incident");
    throw error;
  }
};

// ============================================================================
// INCIDENT ACTION SERVICES
// ============================================================================

/**
 * Fetch incident actions by incident ID
 */
export const fetchIncidentActions = async (
  incidentId: string,
): Promise<ApiResponse<IncidentAction[]>> => {
  try {
    const response: AxiosResponse<ApiResponse<IncidentAction[]>> =
      await axiosInstance.get(`/api/incident-actions/incident/${incidentId}`);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch incident actions");
    throw error;
  }
};

/**
 * Fetch incident actions with query parameters
 */
export const fetchIncidentActionsWithQuery = async (params: {
  incident?: string;
}): Promise<ApiResponse<IncidentAction[]>> => {
  try {
    if (!params.incident) {
      return { success: true, data: [], message: "No incident ID provided" };
    }

    const response: AxiosResponse<ApiResponse<IncidentAction[]>> =
      await axiosInstance.get(
        `/api/incident-actions/incident/${params.incident}`,
      );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch incident actions");
    throw error;
  }
};

/**
 * Get single incident action by ID
 */
export const getIncidentActionById = async (
  actionId: string,
): Promise<ApiResponse<IncidentAction>> => {
  try {
    const response: AxiosResponse<ApiResponse<IncidentAction>> =
      await axiosInstance.get(`/api/incident-actions/${actionId}`);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch action data");
    throw error;
  }
};

/**
 * Create new incident action
 */
export const createIncidentAction = async (
  actionData: any,
): Promise<ApiResponse<IncidentAction>> => {
  try {
    const response: AxiosResponse<ApiResponse<IncidentAction>> =
      await axiosInstance.post(`/api/incident-actions`, actionData);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create incident action");
    throw error;
  }
};

/**
 * Update existing incident action
 */
export const updateIncidentAction = async (
  actionId: string,
  actionData: any,
): Promise<ApiResponse<IncidentAction>> => {
  try {
    const response: AxiosResponse<ApiResponse<IncidentAction>> =
      await axiosInstance.put(`/api/incident-actions/${actionId}`, actionData, {
        timeout: 15000,
      });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update incident action");
    throw error;
  }
};

// ============================================================================
// INCIDENT COMMENT SERVICES
// ============================================================================

/**
 * Create incident comment
 */
export const createIncidentComment = async (commentData: {
  incident: string;
  comment: string;
  commentedBy: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response: AxiosResponse<ApiResponse<any>> = await axiosInstance.post(
      `/api/incident-comments`,
      commentData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create comment");
    throw error;
  }
};

// ============================================================================
// RCA SERVICES
// ============================================================================

/**
 * Fetch RCA data by query parameters
 */
export const fetchRCAData = async (params: {
  incident?: string;
}): Promise<ApiResponse<RCAData[]>> => {
  try {
    let url = `/api/rcas`;
    if ("incident" in params && !params.incident) {
      return { success: true, data: [], message: "No incident ID provided" };
    }

    if (params.incident) {
      const response: AxiosResponse<ApiResponse<RCAData>> =
        await axiosInstance.get(`/api/rcas/incident/${params.incident}`);

      return {
        ...response.data,
        data: response.data.data ? [response.data.data] : [],
      };
    }

    const response: AxiosResponse<ApiResponse<RCAData[]>> =
      await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch RCA data");
    throw error;
  }
};

/**
 * Get RCA by incident ID (returns the RCA linked to a specific incident)
 */
export const getRCAByIncidentId = async (
  incidentId: string,
): Promise<ApiResponse<RCAData>> => {
  try {
    const response: AxiosResponse<ApiResponse<RCAData>> =
      await axiosInstance.get(`/api/rcas/incident/${incidentId}`);
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return { success: false, data: null as any, message: "No RCA found" };
    }
    throw error;
  }
};

/**
 * Get single RCA by ID
 */
export const getRCAById = async (
  rcaId: string,
): Promise<ApiResponse<RCAData>> => {
  try {
    const response: AxiosResponse<ApiResponse<RCAData>> =
      await axiosInstance.get(`/api/rcas/${rcaId}`);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch RCA data");
    throw error;
  }
};

/**
 * Create new RCA
 */
export const createRCA = async (
  rcaData: any,
): Promise<ApiResponse<RCAData>> => {
  try {
    const response: AxiosResponse<ApiResponse<RCAData>> =
      await axiosInstance.post(`/api/rcas`, rcaData);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create RCA");
    throw error;
  }
};

/**
 * Update existing RCA
 */
export const updateRCA = async (
  rcaId: string,
  rcaData: any,
): Promise<ApiResponse<RCAData>> => {
  try {
    const response: AxiosResponse<ApiResponse<RCAData>> =
      await axiosInstance.put(`/api/rcas/${rcaId}`, rcaData);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update RCA");
    throw error;
  }
};

// ============================================================================
// DASHBOARD SERVICES
// ============================================================================

/**
 * Fetch dashboard statistics
 */
export const fetchDashboardStats = async (filters?: {
  regionId?: string;
  premisesId?: string;
}): Promise<ApiResponse<DashboardStatsResponse>> => {
  try {
    let url = `/api/incidents/dashboard/stats`;

    if (filters) {
      const queryParams = new URLSearchParams();

      // Only add parameters if they have specific values (not "All" or empty)
      if (
        filters.regionId &&
        filters.regionId !== "" &&
        filters.regionId !== "All"
      ) {
        queryParams.append("regionId", filters.regionId);
      }

      if (
        filters.premisesId &&
        filters.premisesId !== "" &&
        filters.premisesId !== "All"
      ) {
        queryParams.append("premisesId", filters.premisesId);
      }

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<ApiResponse<DashboardStatsResponse>> =
      await axiosInstance.get(url);

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch dashboard data");
    throw error;
  }
};

// ============================================================================
// FILE SERVICES
// ============================================================================

/**
 * Download incidents as Excel file
 */
export const downloadIncidentsExcel = async (params?: {
  fromDate?: string;
  toDate?: string;
}): Promise<Blob> => {
  try {
    let url = `/api/incidents/export/excel`;

    if (params) {
      const queryParams = new URLSearchParams();
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
      responseType: "blob",
    });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to download incidents");
    throw error;
  }
};

/**
 * Download bulk incidents as Excel file
 */
export const downloadBulkIncidentsExcel = async (params?: {
  fromDate?: string;
  toDate?: string;
  department?: string;
}): Promise<Blob> => {
  try {
    let url = `/api/incidents/export/bulk`;

    if (params) {
      const queryParams = new URLSearchParams();
      if (params.fromDate) queryParams.append("startDate", params.fromDate);
      if (params.toDate) queryParams.append("endDate", params.toDate);
      if (params.department)
        queryParams.append("department", params.department);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }

    const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
      responseType: "blob",
    });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to download bulk incidents report");
    throw error;
  }
};

/**
 * Upload file to S3 (using existing hook functionality)
 * This function serves as a wrapper for the S3 upload functionality
 */
export const uploadFileToS3 = async (file: File): Promise<string> => {
  // This function should integrate with the existing useS3Upload hook
  // For now, we'll throw an error to indicate this needs to be implemented
  // when integrating with the existing S3 upload functionality
  throw new Error(
    "S3 upload functionality should be integrated with existing useS3Upload hook",
  );
};

/**
 * Get signed URL for file access (using existing hook functionality)
 * This function serves as a wrapper for the S3 signed URL functionality
 */
export const getSignedUrl = async (
  filename: string,
  fileType: string,
): Promise<string> => {
  // This function should integrate with the existing S3TempUrl hook
  // For now, we'll throw an error to indicate this needs to be implemented
  // when integrating with the existing S3TempUrl hook
  throw new Error(
    "S3 signed URL functionality should be integrated with existing S3TempUrl hook",
  );
};

// ============================================================================
// EMAIL SERVICES
// ============================================================================

/**
 * Send email notification
 */
export const sendEmail = async (
  emailData: EmailData,
): Promise<ApiResponse<any>> => {
  try {
    const response: AxiosResponse<ApiResponse<any>> = await axiosInstance.post(
      `/api/email`,
      emailData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to send email");
    throw error;
  }
};

/**
 * Send email with simplified parameters
 */
export const sendNotificationEmail = async (params: {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  templateType?: string;
  additionalVariables?: Array<{ key: string; value: string }>;
}): Promise<ApiResponse<any>> => {
  try {
    const emailData: EmailData = {
      to: params.to,
      cc: params.cc || [],
      subject: params.subject,
      body: params.body,
      templateType: params.templateType || "generic",
      additionalVariables: params.additionalVariables || [],
    };

    return await sendEmail(emailData);
  } catch (error) {
    handleApiError(error, "Failed to send notification email");
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Build query parameters for API requests
 */
export const buildQueryParams = (params: Record<string, any>): string => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

/**
 * Handle API response with proper error checking
 */
export const handleApiResponse = <T>(
  response: AxiosResponse<ApiResponse<T>>,
): T => {
  if (response.data.success) {
    return response.data.data;
  } else {
    throw new Error(response.data.message || "API request failed");
  }
};

/**
 * Enrich incidents with action and RCA data
 */
export const enrichIncidentsWithRelatedData = async (
  incidents: Incident[],
): Promise<Incident[]> => {
  try {
    // Fetch all ICS incidents once to check for emergencies
    let icsIncidentsMap: Map<string, boolean> = new Map();
    try {
      const icsResponse = await fetchICSIncidents({ limit: 1000 });
      if (icsResponse.success && icsResponse.data) {
        icsIncidentsMap = new Map(
          icsResponse.data
            .filter(
              (ics: any) => ics.status === "Active" || ics.status === "Standby",
            )
            .map((ics: any) => [ics.incidentId, true]),
        );
      }
    } catch (err) {
      console.warn("Failed to fetch ICS incidents for emergency check:", err);
    }

    const enrichedIncidents = await Promise.all(
      incidents.map(async (incident) => {
        try {
          const incidentId = incident.id || (incident as any)._id;

          if (!incidentId) {
            console.warn("Skipping enrichment: Missing incident ID", incident);
            return incident;
          }

          const rawRca = (incident as any).rca;
          const existingRcaId =
            typeof rawRca === "string"
              ? rawRca
              : rawRca?._id || rawRca?.id || null;

          const actionsResponse = await fetchIncidentActions(incidentId);
          const actions = actionsResponse?.data || [];

          let fetchedRcaId: string | null = null;
          if (!existingRcaId) {
            try {
              const rcaResponse = await fetchRCAData({ incident: incidentId });
              const fetchedRca = rcaResponse?.data?.[0];

              if (
                fetchedRca &&
                (fetchedRca.incident === incidentId ||
                  (fetchedRca.incident as any)?._id === incidentId ||
                  fetchedRca.incident === incident.id)
              ) {
                fetchedRcaId = fetchedRca.id || (fetchedRca as any)._id;
              }
            } catch (err) {
              console.warn(
                `RCA fetch failed for incident ${incidentId}. Using existing RCA if any.`,
              );
            }
          }

          const finalRcaId = existingRcaId || fetchedRcaId;

          // Check if emergency exists - only check if ICS incident has been created
          // The Emergency button should show when there's no ICS incident yet
          const hasICSIncident = icsIncidentsMap.has(incidentId);
          const hasEmergency = hasICSIncident;

          return {
            ...incident,
            id: incidentId,
            hasActions: actions.length > 0,
            actionId:
              actions.length > 0
                ? actions[0].id || (actions[0] as any)._id
                : null,
            hasRca: !!finalRcaId,
            rcaId: finalRcaId,
            hasEmergency,
          };
        } catch (error) {
          console.error(
            `Error enriching incident ${incident?.id || "unknown"}:`,
            error,
          );

          // Fallback: check if ICS incident exists even if enrichment fails
          const hasICSIncident = icsIncidentsMap.has(
            incident.id || (incident as any)._id,
          );

          return {
            ...incident,
            hasActions: false,
            actionId: null,
            hasRca: !!(incident as any).rca,
            rcaId:
              typeof (incident as any).rca === "string"
                ? (incident as any).rca
                : (incident as any).rca?._id || null,
            hasEmergency: hasICSIncident,
          };
        }
      }),
    );

    return enrichedIncidents;
  } catch (error) {
    handleApiError(error, "Failed to enrich incidents with related data");
    throw error;
  }
};

/**
 * Calculate incident statistics
 */
export const calculateIncidentStats = (incidents: Incident[]) => {
  const total = incidents.length;
  const pendingCount = incidents.filter(
    (incident) => incident.status === "Pending",
  ).length;
  const resolvedCount = incidents.filter(
    (incident) => incident.status === "Resolved",
  ).length;
  const resolvedPercentage =
    total > 0 ? Math.round((resolvedCount / total) * 100) : 0;
  const notificationCount = incidents.filter(
    (incident) =>
      incident.rcaStatus === "In Progress" ||
      incident.rcaStatus === "Completed",
  ).length;

  return {
    totalIncident: total,
    pending: pendingCount,
    resolved: resolvedCount,
    totalResolvedPercentage: resolvedPercentage,
    notification: notificationCount,
  };
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Check if an error response is actually a success (for handling API inconsistencies)
 */
export const isSuccessfulResponse = (error: any): boolean => {
  return (
    error.response?.status === 200 ||
    error.response?.data?.success === true ||
    (error.response?.status === 404 &&
      error.response?.data?.message?.includes("successfully"))
  );
};

/**
 * Extract error message from API error response
 */
export const extractErrorMessage = (
  error: any,
  fallbackMessage: string,
): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return fallbackMessage;
  }
};
